/**
 * TextTab Component - Created by david on 17/04/18.
 */
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import messages from './messages'
import {
  Container,
  InputContainer,
  Title,
  ControlButton,
  InputBlockShort,
  AdjustButtonTitle,
  Separator,
  SwitchContainer,
  MultipleGraphic,
  ControlInput,
  Button,
  Input,
  ControlInputContainer,
  DeleteButton,
} from './styledComponents'
import { PositionSize } from '../../../types/common'
import { SCALE_ACTION, ROTATE_ACTION, DRAG_ACTION } from '../Render3D/config'
import Switch from 'antd/lib/switch'

const DECIMAL_REGEX = /[^0-9.]|\.(?=.*\.)/g
const LAST_DOT = /\.$/g

interface Props {
  selectedElement: string
  activeEl: PositionSize
  onCrop: () => void
  onDuplicate: () => void
  onSendToBack: () => void
  onSendToFront: () => void
  onMirror: () => void
  onDeleteLayer: (id: string) => void
  handleChange: (data: PositionSize, type: string) => void
  toggleElementLock: () => void
  locked: boolean
}

interface State {
  aspectLock: boolean
}

const PlusIcon = 'https://storage.googleapis.com/jakroo/clipart/plus-icon.svg'
const MinusIcon = 'https://storage.googleapis.com/jakroo/clipart/minus-icon.svg'
const LinkIcon = 'https://storage.googleapis.com/jakroo/clipart/link-angled.svg'
const LinkBroken =
  'https://storage.googleapis.com/jakroo/clipart/link-broken.svg'
const ChevronIcon =
  'https://storage.googleapis.com/jakroo/clipart/chevron-up.svg'

type ActionIconProps = {
  src: string
  angle?: number
  width?: number
  height?: number
}

const ActionIcon: React.FC<ActionIconProps> = ({
  src,
  angle = 0,
  width = 30,
  height = 30,
}) => (
  <img
    src={src}
    decoding="async"
    width={width}
    height={height}
    style={{ transform: `rotate(${angle}deg)` }}
  />
)

export class PositionResize extends React.PureComponent<Props, State> {
  state = {
    aspectLock: true,
  }

  handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { currentTarget } = event || {}
    const { field, dir } = currentTarget.dataset
    if (!field || !dir) {
      return
    }
    const container = document.getElementById(field)
    if (!container) {
      return
    }
    const spanSelector =
      dir === 'up'
        ? '.ant-input-number-handler-up'
        : '.ant-input-number-handler-down'
    const targetSpan = container.querySelector<HTMLSpanElement>(spanSelector)
    if (targetSpan) {
      const mouseDownEvent = new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
      })
      const mouseUpEvent = new MouseEvent('mouseup', {
        bubbles: true,
        cancelable: true,
      })
      targetSpan.dispatchEvent(mouseDownEvent)
      targetSpan.dispatchEvent(mouseUpEvent)
    } else {
      console.warn(`Could not find ${spanSelector} in container #${container}`)
    }
  }

  changeValue = (name: string, type: string) => (
    value: number | undefined = 0
  ) => {
    const { handleChange, activeEl } = this.props
    handleChange({ ...activeEl, [name]: value }, type)
  }

  round1 = (val: number) => Math.round((val + Number.EPSILON) * 10) / 10

  changeWidth = (widthVal: number | undefined = 1) => {
    const { aspectLock } = this.state
    const { handleChange, activeEl } = this.props
    const { height: originalHeight = 0, width: originalWidth = 0 } = activeEl
    const width = this.round1(widthVal || 0)
    const originalWidthValue = isNaN(originalWidth) ? 0 : originalWidth
    const originalHeightValue = isNaN(originalHeight) ? 0 : originalHeight
    let height = originalHeightValue
    if (aspectLock && originalWidthValue) {
      const aspectRatio = originalHeightValue / originalWidthValue
      height = this.round1(width * aspectRatio)
    }
    handleChange(
      {
        ...activeEl,
        height,
        width,
      },
      SCALE_ACTION
    )
  }

  changeHeight = (heightVal: number | undefined = 1) => {
    const { aspectLock } = this.state
    const { handleChange, activeEl } = this.props
    const { height: originalHeight = 0, width: originalWidth = 0 } = activeEl
    const height = this.round1(heightVal || 0)
    const originalHeightValue = isNaN(originalHeight) ? 0 : originalHeight
    const originalWidthValue = isNaN(originalWidth) ? 0 : originalWidth
    let width = originalWidthValue
    if (aspectLock && originalHeightValue) {
      const aspectRatio = originalWidthValue / originalHeightValue
      width = this.round1(height * aspectRatio)
    }
    handleChange(
      {
        ...activeEl,
        height,
        width,
      },
      SCALE_ACTION
    )
  }

  horizontalMirror = () => {
    const { activeEl, handleChange } = this.props
    const { width = 0 } = activeEl
    const widthValue = isNaN(width) ? 0 : width * -1
    handleChange({ ...activeEl, width: widthValue }, SCALE_ACTION)
  }

  changeLock = () => {
    this.setState(({ aspectLock }) => ({
      aspectLock: !aspectLock,
    }))
  }

  parser = (value: string | undefined) => {
    const newValue = value ? value.replace(DECIMAL_REGEX, '') : ''
    return newValue
  }

  format = (value: string) => {
    const newValue =
      value && !LAST_DOT.test(value)
        ? Math.round(parseFloat(value) * 10) / 10
        : value
    return `${newValue.toString()} cm`
  }

  render() {
    const { aspectLock } = this.state
    const {
      activeEl = {} as any,
      onCrop,
      onDuplicate,
      onSendToBack,
      onSendToFront,
      onMirror,
      onDeleteLayer,
      selectedElement,
      toggleElementLock,
      locked,
    } = this.props
    const {
      width = 0,
      height = 0,
      horizontal = 0,
      vertical = 0,
      rotation = 0,
    } = activeEl || {}
    const realWidth = isNaN(width) ? 0 : width
    const realHeight = isNaN(height) ? 0 : height
    const realHorizontal = isNaN(horizontal) ? 0 : horizontal
    const realVertical = isNaN(vertical) ? 0 : vertical
    const realRotation = isNaN(rotation) ? 0 : rotation
    return (
      <Container>
        <Title>
          <FormattedMessage {...messages.scale} />
        </Title>
        <InputContainer>
          <ControlInputContainer>
            <FormattedMessage {...messages.width} />
            <ControlInput id="width">
              <Button
                data-field="width"
                data-dir="down"
                onClick={this.handleClick}
              >
                <ActionIcon src={MinusIcon} />
              </Button>
              <Input
                type="number"
                value={Math.round(realWidth * 10) / 10}
                step={0.1}
                onChange={this.changeWidth}
              />
              <Button
                data-field="width"
                data-dir="up"
                onClick={this.handleClick}
              >
                <ActionIcon src={PlusIcon} />
              </Button>
            </ControlInput>
          </ControlInputContainer>
          <ControlInputContainer style={{width: 'fit-content', alignItems: 'center'}}>
            <FormattedMessage {...messages.constraints} />
            <button
              style={{
                padding: '0px',
                border: 'none',
                borderRadius: '4px',
                width: '46px',
                height: '38px',
                cursor: 'pointer',
                backgroundColor: aspectLock ? '#E8E5E5' : '#27E1FD',
              }}
              onClick={this.changeLock}
            >
              <ActionIcon
                src={aspectLock ? LinkIcon : LinkBroken}
                width={26}
                height={26}
              />
            </button>
          </ControlInputContainer>
          <ControlInputContainer>
            <FormattedMessage {...messages.height} />
            <ControlInput id="height">
              <Button
                data-field="height"
                data-dir="down"
                onClick={this.handleClick}
              >
                <ActionIcon src={MinusIcon} />
              </Button>
              <Input
                type="number"
                value={Math.round(realHeight * 10) / 10}
                step={0.1}
                onChange={this.changeHeight}
              />
              <Button
                data-field="height"
                data-dir="up"
                onClick={this.handleClick}
              >
                <ActionIcon src={PlusIcon} />
              </Button>
            </ControlInput>
          </ControlInputContainer>
        </InputContainer>
        <Separator />
        <Title>
          <FormattedMessage {...messages.position} />
        </Title>
        <InputContainer>
          <ControlInputContainer>
            <FormattedMessage {...messages.horizontal} />
            <ControlInput id="horizontal">
              <Button
                data-field="horizontal"
                data-dir="down"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} angle={-90} />
              </Button>
              <Input
                id="horizontal"
                value={realHorizontal}
                formatter={(rawValue) => `${Math.round(rawValue)} px`}
                parser={(value) => value.replace(DECIMAL_REGEX, '')}
                precision={0}
                onChange={this.changeValue('horizontal', DRAG_ACTION)}
              />
              <Button
                data-field="horizontal"
                data-dir="up"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} angle={90} />
              </Button>
            </ControlInput>
          </ControlInputContainer>
          <ControlInputContainer>
            <FormattedMessage {...messages.vertical} />
            <ControlInput id="vertical">
              <Button
                data-field="vertical"
                data-dir="down"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} />
              </Button>
              <Input
                value={realVertical}
                formatter={(rawValue) => `${Math.round(rawValue)} px`}
                parser={(value) => value.replace(DECIMAL_REGEX, '')}
                precision={0}
                onChange={this.changeValue('vertical', DRAG_ACTION)}
              />
              <Button
                data-field="vertical"
                data-dir="up"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} angle={180} />
              </Button>
            </ControlInput>
          </ControlInputContainer>
          <ControlInputContainer>
            <FormattedMessage {...messages.rotation} />
            <ControlInput id="rotation">
              <Button
                data-field="rotation"
                data-dir="down"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} />
              </Button>
              <Input
                value={realRotation}
                min={0}
                max={360}
                formatter={(rawValue) => `${rawValue}º`}
                parser={(value) => value.replace('º', '')}
                precision={0}
                onChange={this.changeValue('rotation', ROTATE_ACTION)}
              />
              <Button
                data-field="rotation"
                data-dir="up"
                onClick={this.handleClick}
              >
                <ActionIcon src={ChevronIcon} angle={180} />
              </Button>
            </ControlInput>
          </ControlInputContainer>
        </InputContainer>
        <Separator />
        <Title>
          <FormattedMessage {...messages.adjust} />
        </Title>
        <InputContainer style={{flexWrap: 'wrap'}}>
          <InputBlockShort style={{ display: 'none' }}>
            <AdjustButtonTitle>
              <FormattedMessage {...messages.crop} />
            </AdjustButtonTitle>
            <ControlButton onClick={onCrop}
              src="https://storage.googleapis.com/jakroo/clipart/crop.svg" />
          </InputBlockShort>
          <InputBlockShort>
            <AdjustButtonTitle>
              <FormattedMessage {...messages.duplicate} />
            </AdjustButtonTitle>
            <ControlButton onClick={onDuplicate}
              src="https://storage.googleapis.com/jakroo/clipart/adjust_duplicate.svg" />
          </InputBlockShort>
          <InputBlockShort>
            <AdjustButtonTitle>
              <FormattedMessage {...messages.sendBack} />
            </AdjustButtonTitle>
            <ControlButton onClick={onSendToBack}
              src="https://storage.googleapis.com/jakroo/clipart/send_back.svg" />
          </InputBlockShort>
          <InputBlockShort>
            <AdjustButtonTitle>
              <FormattedMessage {...messages.bringFront} />
            </AdjustButtonTitle>
            <ControlButton onClick={onSendToFront}
              src="https://storage.googleapis.com/jakroo/clipart/bring_front.svg" />
          </InputBlockShort>
          <InputBlockShort>
            <AdjustButtonTitle>
              <FormattedMessage {...messages.mirror} />
            </AdjustButtonTitle>
            <ControlButton onClick={onMirror}
              src="https://storage.googleapis.com/jakroo/clipart/mirror.svg" />
          </InputBlockShort>
        </InputContainer>
        <Separator />
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: '12px',
            marginBottom: '12px',
            paddingLeft: '8px',
          }}
        >
          <SwitchContainer checked={locked}>
            <AdjustButtonTitle>
              {locked ? 'Locked' : 'Unlocked'}
            </AdjustButtonTitle>
            <Switch
              size="default"
              onChange={toggleElementLock}
              checked={locked}
            />
          </SwitchContainer>
          <MultipleGraphic>
            <div
              style={{
                color: '#8F8889',
                width: '100%',
                textAlign: 'center',
                fontSize: '14px',
                fontWeight: 'bold',
              }}
            >
              Select multiple graphics {'>'}
            </div>
          </MultipleGraphic>
        </div>
        <Separator />
        <DeleteButton onClick={() => onDeleteLayer(selectedElement)}>
          Delete
        </DeleteButton>
      </Container>
    )
  }
}

export default PositionResize



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/components/DesignCenterCustomize/PositionResize/index.tsx